import { Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormInputFragment } from 'src/app/fragments/form-input/form-input.fragment';
import { ModalService } from 'src/app/services/modal/modal.service';
import { CabinetService } from 'src/app/services/office/cabinet.service';
import { ProfilService } from 'src/app/services/profil/profil.service';
import { Cabinet, EtatDeploiementEnum } from 'src/app/utils/interfaces/new/cabinet.interface';
import { CreateCabinet } from 'src/app/utils/interfaces/new/create-cabinet.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { ModaleOpsCabinetDeploymentComponent } from '../modale-ops-cabinet-deployment/modale-ops-cabinet-deployment.component';
import { FeatureType } from 'src/app/services/feature/feature.service';
import { MigrationComponent, NgSelectValue } from 'src/app/fragments/modal/migration/migration.component';

@Component({
  selector: 'app-office-creation',
  templateUrl: './office-creation.component.html',
  styleUrls: ['./office-creation.component.scss']
})
export class OfficeCreationComponent implements OnInit {

  @ViewChild('isMsp') public isMsp: FormInputFragment;
  @ViewChild('isCs') public isCs: FormInputFragment;

  public officeForm: FormGroup;
  public profilColumnTable = [];
  public profils: Paginated<Profil>;
  public cabinetId: string;
  public cabinetInfos: Cabinet;
  public deployStatusView: string;
  public filtre: Filtres = {
    perpage: 10,
    currentPage: 1
  };

  private _certDmp: string;
  private _certInsi: string;

  constructor(
    private officeService: CabinetService,
    private formBuilder: FormBuilder,
    private profilService: ProfilService,
    private cabinetService: CabinetService,
    public viewContainerRef: ViewContainerRef,
    public modalService: ModalService,
    public router: Router,
    private route: ActivatedRoute,
  ) { }

  async ngOnInit(): Promise<void> {
    this.cabinetId = this.route.snapshot.paramMap.get('id');
    this.profilColumnTable = [
      { title: 'NOM Prénom', data: 'nom' },
      { title: 'Email/Login', data: 'userEmail'},
      { title: 'Rôle(s)', data: 'rolesStr' },
      { title: 'Auth(Temp)', data: 'nbAuthentificationStr' },
      { title: 'Activer/Désactiver', data: null, styleWidth: '1%'},
      { title: 'Modifier', data: null, styleWidth: '1%' },
      { title: 'Réinitialiser le mot de passe', data: null, styleWidth: '1%'}
    ];

    if (this.cabinetId && this.cabinetId !== 'create') {
      const loadingModal = this.modalService.openLoading(this.viewContainerRef);
      this.cabinetInfos = await this.officeService.findOne(this.cabinetId);
      switch (this.cabinetInfos.etatDeploiement) {
        case EtatDeploiementEnum.Deploye:
          this.deployStatusView = "🟢 Déployé"
          break;
        case EtatDeploiementEnum.EnCours:
          this.deployStatusView = "🟠 En cours"
          break;
        case EtatDeploiementEnum.Erreur:
          this.deployStatusView = "⚠️ En erreur"
          break;
        case EtatDeploiementEnum.NonDeploye:
          this.deployStatusView = "🔴 Non déployé"
          break;
      }
      await this.getCabinetProfils();
      this.modalService.close(loadingModal);
    }
    this.setOfficeValue();
  }

  private setOfficeValue(): void {
    if (this.cabinetInfos) {
      this.officeForm = this.formBuilder.group({
        officeName: [this.cabinetInfos.nom, Validators.required],
        address: [this.cabinetInfos.adresse, Validators.required],
        postalCode: [this.cabinetInfos.codePostal, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
        city: [this.cabinetInfos.ville, Validators.required],
        isMsp: [this.cabinetInfos.type === "msp"],
        isCs: [this.cabinetInfos.type === "cs"],
        emailAdmin: [this.cabinetInfos.emailAdmin ? this.cabinetInfos.emailAdmin : '', this.emptyEmailValidator],
        certificatDmpExists: [this.cabinetInfos.certificatDmpExists],
        certificatInsiExists: [this.cabinetInfos.certificatInsiExists]
      });
    } else {
      this.officeForm = this.formBuilder.group({
        officeName: ['', Validators.required],
        address: ['', Validators.required],
        postalCode: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
        city: ['', Validators.required],
        isMsp: [false],
        isCs: [false],
        emailAdmin: ['', this.emptyEmailValidator],
        certificatDmpExists: [false],
        certificatInsiExists: [false]
      });
    }
  }

  public async onSubmit(): Promise<void> {
    const loader = this.modalService.openLoading(this.viewContainerRef);
    try {
      this.officeForm.markAllAsTouched();
      if (this.officeForm.valid) {
        if (this.cabinetId && this.cabinetId !== 'create') {
          const cabinet: Cabinet = {
            id: this.cabinetId,
            nom: this.officeForm.get('officeName').value,
            adresse: this.officeForm.get('address').value,
            codePostal: this.officeForm.get('postalCode').value,
            ville: this.officeForm.get('city').value,
            type: this.getTypeOffice(),
            emailAdmin: this.officeForm.get('emailAdmin').value ? this.officeForm.get('emailAdmin').value : '',
            disabled: false
          };

          if (this._certDmp || this._certDmp === '') {
            (cabinet as any).certificatDmp = this._certDmp;
          }

          if (this._certInsi || this._certInsi === '') {
            (cabinet as any).certificatInsi = this._certInsi;
          }

          await this.officeService.update(cabinet);
          this.modalService.close(loader);
          this.router.navigateByUrl('/administration/office-management');
        } else {
          const cabinet: CreateCabinet = {
            nom: this.officeForm.get('officeName').value,
            adresse: this.officeForm.get('address').value,
            codePostal: this.officeForm.get('postalCode').value,
            ville: this.officeForm.get('city').value,
            type: this.getTypeOffice(),
            emailAdmin: this.officeForm.get('emailAdmin').value ? this.officeForm.get('emailAdmin').value : '',
            disabled: false
          };
          const id = await this.officeService.create(cabinet);
          this.modalService.close(loader);
          this.reloadComponent(id);
        }
      } else {
        this.modalService.close(loader);
      }
    } catch (e) {
      this.modalService.close(loader);
    }
  }

  public reloadComponent(id: string): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([`administration/office-creation/${id}`]);
  }

  public async deleteOffice(): Promise<void> {
    console.log('Delete office');
  }

  public startDeploy(): void {
    const modal = this.modalService.openModal<ModaleOpsCabinetDeploymentComponent>(
      this.viewContainerRef,
      ModaleOpsCabinetDeploymentComponent
    ) as ComponentRef<ModaleOpsCabinetDeploymentComponent>;

    modal.instance.cabinetId = this.cabinetId;

    const sub = modal.instance.closeEmitter.subscribe(() => {
      modal.destroy();
      sub.unsubscribe();
      this.ngOnInit();
    });
  }

  public async removeProfilFromOffice(profil: Profil): Promise<void> {
    const loading = this.modalService.openLoading(this.viewContainerRef);
    try {
      const profilComplet = await this.profilService.findOne(profil.id);
      profil = profilComplet;

      if (profil.disabled) {
        await this.profilService.enableProfil(this.formatProfil(profil));
      } else {
        await this.profilService.disableProfil(this.formatProfil(profil));
      }
      await this.getCabinetProfils();
    } catch (e) {
      this.modalService.openAlert(this.viewContainerRef, 'Erreur', e.message);
    } finally {
      loading.destroy();
    }
  }

  public async resetPassword(profil: Profil): Promise<void> {
    const result = await this.profilService.resetPassword(profil.id);
    if (result) {
      this.modalService.openAlert(this.viewContainerRef, 'Reinitialisation de mot de passe', 'Le mot de passe de l\'utilisateur a bien été réinitialisé\n\nUn email lui a été envoyé avec un nouveau mot de passe temporaire. Il devra le changer à sa prochaine connexion.');
    }
  }

  private formatProfil(object: any): Profil {
    const profil: Profil = {
      id: object.id,
      idUtilisateur: object.idUtilisateur,
      idCabinet: object.idCabinet,
      qualite: object.qualite,
      specialite: object.specialite,
      adresse: object.adresse,
      ville: object.ville,
      codePostal: object.codePostal,
      telephone: object.telephone,
      disabled: object.disabled,
      actif: object.actif,
      adminCabinet: object.adminCabinet,
      roles: object.roles,
    };
    return profil;
  }

  public async editProfilFromOffice(profil: Profil): Promise<void> {
    this.router.navigateByUrl(`/administration/user-creation/${this.cabinetId}/${profil.id}`);
  }

  public addUser(): void {
    this.router.navigateByUrl(`/administration/user-creation/${this.cabinetId}/new`);
  }

  public async onSort(event: any): Promise<void> {
    this.filtre.currentPage = 1;
    this.filtre.sortOrder = event.sortOrder;
    this.filtre.sortBy = event.sortBy;
    this.getCabinetProfils();
  }

  public async onSearch(event: any): Promise<void> {
    this.filtre.currentPage = 1;
    this.filtre.find = event;
    this.getCabinetProfils();
  }

  public async onChangePage(value: any): Promise<void> {
    this.filtre.currentPage = value;
    this.getCabinetProfils();
  }

  public async getCabinetProfils(): Promise<void> {
    this.profils = await this.cabinetService.findCabinetProfils(this.cabinetId, this.filtre);
    for (const profil of this.profils.objects) {
      (profil as any).nom = (profil.utilisateur.nom || "").toUpperCase() +
        ' ' +
        (profil.utilisateur.prenom || "").charAt(0).toUpperCase() + (profil.utilisateur.prenom || "").slice(1).toLowerCase();

      if (profil.disabled) {
        Object.defineProperty(profil, 'rowColor', { configurable: true, value: 'var(--grey0)' });
        Object.defineProperty(profil, 'textColor', { configurable: true, value: 'var(--black)' });
        Object.defineProperty(profil, 'active1', { configurable: true, value: 'var(--danger)' });
        Object.defineProperty(profil, 'button1Icon', { configurable: true, value: 'assets/icons/cross.svg' });
        Object.defineProperty(profil, 'button1Title', { value: 'Profil désactivé' });
      } else {
        // Object.defineProperty(profil, 'active1', { configurable: true, value: 'var(--primary)' });
        Object.defineProperty(profil, 'button1Icon', { configurable: true, value: 'assets/icons/green-checkmark.svg' });
        Object.defineProperty(profil, 'button1Title', { value: 'Profil activé' });
      }

      if (profil.utilisateur) {
        Object.defineProperty(profil, 'userEmail', { value: profil.utilisateur.email });
      }

      if (profil.roles) {
        Object.defineProperty(profil, 'rolesStr', { value: profil.roles ? profil.roles.map((role: any) => role.nom).join('\n') : '' });
      }

      if (profil.nbAuthentification != null && profil.nbTempAuthentification != null) {
        Object.defineProperty(profil, 'nbAuthentificationStr', { value: profil.nbAuthentification + ' (' + profil.nbTempAuthentification + ')'});
      }
    }
  }
  public getTypeOffice(): string | undefined {
    if (this.officeForm.get("isMsp").value) {
      return "msp";
    } else if (this.officeForm.get("isCs").value) {
      return "cs";
    } else {
      return "";
    }
  }
  public changeMsp(): void {
    if (this.officeForm.get("isMsp").value) {
      this.officeForm.get("isCs").setValue(false);
    }
  }
  public changeCs(): void {
    if (this.officeForm.get("isCs").value) {
      this.officeForm.get("isMsp").setValue(false);
    }
  }

  private emptyEmailValidator(control: AbstractControl): ValidationErrors {
    if (!control.value) {
      return null;
    }

    return Validators.email(control);
  }

  public async addCertificat(type: 'DMP' | 'INSI'): Promise<void> {
    const _getFile = (): Promise<File | undefined> => {
      return new Promise<File | undefined>(resolve => {
        try {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.dat';
          document.body.appendChild(input);
          input.addEventListener('change', (event: any) => {
            const file = (event?.target?.files as FileList)?.[0] as File;
            resolve(file || undefined);
            document.body.removeChild(input);
          });
          input.click();
        } catch (_) {
          resolve(undefined);
        }
      });
    };

    const _convertFileToBase64 = (file: File): Promise<string | undefined> => {
      return new Promise<string | undefined>(resolve => {
        try {
          var reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = function () {
            resolve(reader.result as string);
          };
        } catch (_) {
          resolve(undefined);
        }
      });
    };

    const _unprefixBase64 = (b64: string): string => {
      const b64PrefixNeedle = ';base64,';
      if (b64.includes(b64PrefixNeedle)) {
        return b64.split(b64PrefixNeedle)[1];
      } else {
        return b64;
      }
    };

    const _getCertificat = async (): Promise<string | undefined> => {
      let rtd: string;
      const file = await _getFile();
      if (file) {
        const b64 = await _convertFileToBase64(file);
        if (b64) {
          rtd = _unprefixBase64(b64);
        }
      }
      return rtd || undefined;
    };

    const cert = await _getCertificat();
    if (cert && cert !== '') {
      if (type === 'DMP') {
        if (this.officeForm.get("certificatDmpExists")) {
          this.officeForm.get("certificatDmpExists").setValue(true);
        }
        this.cabinetInfos.certificatDmpExists = true;
        this._certDmp = cert;
      } else if (type === 'INSI') {
        if (this.officeForm.get("certificatInsiExists")) {
          this.officeForm.get("certificatInsiExists").setValue(true);
        }
        this.cabinetInfos.certificatInsiExists = true;
        this._certInsi = cert;
      }
    }
  }

  public supprimer(type: 'DMP' | 'INSI'): void {
    const modal = this.modalService.openModalConfirmation(
      this.viewContainerRef,
      'Suppression certificat',
      `Voulez-vous supprimer le certificat ${type} établissement ?`
    );
    const sub = modal.instance.closeEmitter.subscribe(v => {
      sub.unsubscribe();
      if (v) {
        if (type === 'DMP' && this.officeForm.get("certificatDmpExists")) {
          this.officeForm.get("certificatDmpExists").setValue(false);
          this.cabinetInfos.certificatDmpExists = false;
          this._certDmp = '';
        }
        if (type === 'INSI' && this.officeForm.get("certificatInsiExists")) {
          this.officeForm.get("certificatInsiExists").setValue(false);
          this.cabinetInfos.certificatInsiExists = false;
          this._certInsi = '';
        }
      }
    });
  }

  public goToFeaturesManagment(): void {
    this.router.navigateByUrl(`/administration/feature-flipping?type=${FeatureType.CABINET}&id=${this.cabinetId}`);
  }

  public async openMigration(): Promise<void> {
    let profils: Paginated<Profil>;
    const loading = this.modalService.openLoading(this.viewContainerRef);
    try {
      profils = await this.cabinetService.findCabinetProfils(this.cabinetId, {
        perpage: -1,
        currentPage: -1
      });
    } catch (e) {
      this.modalService.openAlert(this.viewContainerRef, 'Erreur', e.message);
    } finally {
      loading.destroy();
    }

    const migrationModal = this.modalService.openModal(
      this.viewContainerRef,
      MigrationComponent,
    );

    migrationModal.instance.cabinetUsers = profils.objects.map(profil => {
      return {
        id: profil.id,
        libelle: profil.utilisateur?.nom + ' ' + profil.utilisateur?.prenom,
      } as NgSelectValue;
    });

    migrationModal.instance.cabinetName = this.officeForm.get('officeName').value;

    migrationModal.instance.infosCabinet = {
      idCabinet: this.cabinetId,
      authBucket: 'TODO',
      ivBucket: 'TODO',
    }

    const sub = migrationModal.instance.closeEmitter.subscribe(() => {
      migrationModal.destroy();
      sub.unsubscribe();
    })
  }
}

